import { useForm } from 'react-hook-form'
import {
  filtersConfig,
  filterElement,
} from '~/features/BookingCars/ListBookingCars/config'
import { BookingCar, Region } from '~/shared/api'
import { Button } from '~/shared/ui/Buttons'
import { FormProvider } from '~/shared/ui/form'
import {
  DependentSelects,
  AsyncSelect,
  MultiButtons,
  NumberRange,
  useFiltersQueryParams,
  YearRange,
  sanitizeFilterFields,
} from '~/shared/ui/ScrollableList'
import { ApplyButton } from './ApplyButton'
import { Locations } from './Locations'
import { DrawerWrapper, Line, Title, ButtonsWrapper } from './styled'
import { useDependentBrandModelOptions } from './useDependentBrandModelOptions'

const {
  region,
  taxipark,
  location,
  brandModel,
  price,
  yearn,
  transmission,
  fuelType,
  vehicleCategory,
} = filterElement

type ContentProps = {
  onClose: () => void
}

export function Content({ onClose }: ContentProps) {
  const [filtersQuery, setFiltersQuery] = useFiltersQueryParams(filtersConfig)

  const form = useForm({
    defaultValues: filtersQuery,
  })

  const { setValue, watch } = form

  const formValues = watch()
  const taxiparkId = formValues[taxipark.field]?.id
  const regionCode = formValues[region.field]?.id
  const locationIds = (formValues[location.field] || []).map(
    ({ id }: { id: UniqueId }) => id,
  )

  const selectedBrands = formValues[brandModel.first.field]

  const handleOnClear = () => {
    const emptyFields = sanitizeFilterFields(filtersConfig)
    emptyFields.forEach((field) => setValue(field, undefined))
  }

  const handleOnSuccess = (data: Record<string, unknown>) => {
    setFiltersQuery(data, 'replaceIn')
    onClose()
  }

  const handlePriceBrandModelReset = () => {
    setValue(price.option.fromField, undefined)
    setValue(price.option.toField, undefined)
    setValue(brandModel.first.field, undefined)
    setValue(brandModel.second.field, undefined)
  }

  const handleRegionChange = () => {
    setValue(location.field, undefined)
    handlePriceBrandModelReset()
  }

  const handleLocationsChange = () => {
    handlePriceBrandModelReset()
  }

  const { isFetchingDependentBrandOptions, brandOptions, modelOptions } =
    useDependentBrandModelOptions({
      regionCode,
      locationIds,
      selectedBrands,
      taxiparkId,
    })

  return (
    <FormProvider form={form} onSuccess={handleOnSuccess}>
      <DrawerWrapper>
        <Title>Фильтры</Title>

        <div>
          <div style={{ marginBottom: '8px' }}>
            <AsyncSelect
              {...region}
              fetchOptions={() =>
                Region.fetchOptions(
                  taxiparkId,
                  !taxiparkId
                    ? [{ key: 'allRegions', value: 'true' }]
                    : undefined,
                )
              }
              onChange={handleRegionChange}
              canClear
            />
          </div>
          <Locations regionCode={regionCode} onChange={handleLocationsChange} />
        </div>
        <Line />

        <DependentSelects
          {...brandModel}
          first={{ ...brandModel.first, options: brandOptions }}
          second={{ ...brandModel.second, options: modelOptions }}
          isLoading={isFetchingDependentBrandOptions}
        />
        <Line />

        <NumberRange
          {...price}
          fetchOptions={() =>
            BookingCar.getPrices({ regionCode, locationIds, taxiparkId })
          }
          queryKey={`${regionCode}_${locationIds}`}
        />
        <Line />
        <YearRange {...yearn} />
        <Line />
        <MultiButtons {...transmission} />
        <Line />
        <MultiButtons {...fuelType} />
        <Line />
        <MultiButtons {...vehicleCategory} />
        <Line />
      </DrawerWrapper>

      <ButtonsWrapper>
        <Button
          fullWidth
          variant='lightSteelBlueBorder'
          onClick={handleOnClear}
        >
          Сбросить
        </Button>

        <ApplyButton filtersQuery={formValues} />
      </ButtonsWrapper>
    </FormProvider>
  )
}
